import { Grid, Typography } from '@mui/material'
import React, { useContext, useState } from 'react'

import style from '../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

import { getCampaignContactListResponse, globalMenuItemData } from '../../../models/axiosModel';
import { formatDateAccordingToTimezoneUsingMoment, hasCampaignManageAccess, hasContactManageAccess } from '../../../utils/constants';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { useGenericStore } from '../../../store/genericStore';
import { useNavigate } from 'react-router-dom';
import { isArchivedContactList, isMasterContactList } from './contactConstant';
import GlobalMenu from '../../globalMenu';
import GlobalTooltip from '../../utility/tooltip/globalTooltip';

export type contactListMenuItem = Omit<globalMenuItemData, 'isDisable'> & {
    isDisable?: boolean | ((list: getCampaignContactListResponse) => boolean);
    isHidden?: (list: getCampaignContactListResponse) => boolean;
}

interface ListCardProps {
    list: getCampaignContactListResponse,
    onClickHandler: (id: number) => void,
    menuItem: contactListMenuItem[],
}

const ListCard: React.FC<ListCardProps> = ({ list, onClickHandler, menuItem }) => {

    const { userDetail, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload;
    const globalStatusData = useGenericStore((state) => state.listOfChatbotStatus);

    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);

    const isArchived = isArchivedContactList(list, globalStatusData);
    const isSystemList = isMasterContactList(list?.contact_list_name);
    
    const visibleMenuItems = menuItem
        .filter((item) => !item.isHidden?.(list))
        .map(({ isHidden, ...item }): globalMenuItemData => ({
            ...item,
            isDisable: typeof item.isDisable === 'function'
                ? item.isDisable(list)
                : item.isDisable,
        }));

    const showMenu = hasContactManageAccess(apiRoutesPermissionAssignment, userDetail) && !isSystemList && (visibleMenuItems.length > 0);

    const navigate = useNavigate();

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    }

    const handleMenuClose = () => {
        setAnchorEl(null);
    }

    return (
        <Grid
            container
            onClick={() => { onClickHandler(list.contact_list_id) }}
            className={`${style.contactListContainer} ${isArchived ? style.contactListContainerArchived : ""}`}
        >
            {/* Header */}
            <Grid item xs={12} className={`${style.contactListHeaderRow} ${isArchived ? style.contactListHeaderRowArchived : ""}`}>
                <Grid item className={style.contactListIconBadge}>
                    {isArchived
                        ? <ArchiveOutlinedIcon className={style.contactListHeaderIcon} />
                        : <PeopleAltOutlinedIcon className={style.contactListHeaderIcon} />
                    }
                </Grid>

                <Grid item className={style.contactListHeaderRight}>
                    {isSystemList && 
                        <Grid item className={style.systemTagForMasterContactList}>
                            System
                        </Grid>
                    }
                    {isArchived && !isSystemList && 
                        <Grid item className={style.archivedTagForContactList}>
                            Archived
                        </Grid>
                    }
                    {showMenu && 
                        <Grid item className={style.morevertIconGridItem}>
                            <MoreVertIcon
                                className={style.contactListMoreVertIcon}
                                onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => { handleMenuOpen(e); }}
                            />
                            <GlobalMenu
                                anchorEl={anchorEl}
                                handleMenuClose={handleMenuClose}
                                menuItemData={visibleMenuItems}
                                open={Boolean(anchorEl)}
                                additionalField={list}
                                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                            />
                        </Grid>
                    }
                </Grid>
            </Grid>

            {/* Body */}
            <Grid item xs={12} className={style.contactListBody}>
                <Typography className={`${style.contactListNameTitle} ${isArchived ? style.contactListNameTitleArchived : ""}`}>
                    {list.contact_list_name}
                </Typography>

                <Grid item className={style.contactListMetaRow}>
                    <Grid item className={style.contactListMetaChip}>
                        <ContactsOutlinedIcon className={style.contactListMetaIcon} />
                        <Typography className={style.contactTitleTypography}>
                            {list.total_contact} contacts
                        </Typography>
                    </Grid>

                    <GlobalTooltip
                        title="Last updated at"
                        placement="top"
                        arrow
                    >
                        <Grid item className={style.contactListMetaChip}>
                                <AccessTimeOutlinedIcon className={style.contactListMetaIcon} />
                                <Typography className={style.timezoneTitleTypography}>
                                    {formatDateAccordingToTimezoneUsingMoment(
                                        list.updated_at_timestamp != null ? list.updated_at_timestamp : list.created_at_timestamp,
                                        userDetail.timezone_hour
                                    )}
                                </Typography>
                        </Grid>
                    </GlobalTooltip>
                </Grid>
            </Grid>

            {/* Footer */}
            {hasCampaignManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                <Grid
                    item
                    xs={12}
                    className={`${style.runCampaignGrid} ${(list.total_contact === 0 || isArchived) ? style.runCampignGridDisable : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if(list.total_contact > 0 && !isArchived){
                            navigate(`/whatsappcampaign/createnewcampaign?list_id=${list.contact_list_id}`);
                        }
                    }}
                >
                    <PlayArrowRoundedIcon className={style.playIcon} />
                    <Typography className={style.contactTitleTypography}>Run campaign</Typography>
                </Grid>
            }

        </Grid>
    )
}

export default ListCard
